// retry.interceptor.ts
import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, timer, throwError } from 'rxjs';
import { retry, mergeMap } from 'rxjs/operators';

export enum RetryableHttpStatus {
	NetworkError = 0,
	TooManyRequests = 429,
	BadGateway = 502,
	ServiceUnavailable = 503,
	GatewayTimeout = 504,
}

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
	private readonly maxRetries = 3;
	private readonly baseDelayMs = 300;

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// THIS IDEMPOTENT METHODS ARE THE ONLY ONES FOR WHICH WE RETRY
		const isIdempotent = req.method === 'POST' || req.method === 'GET' || req.method === 'PUT' || req.method === 'DELETE';

		return next.handle(req).pipe(
			retry({
				count: this.maxRetries,
				delay: (error: unknown, retryCount: number) => {
					//If the error returned from the API Gateway is handled exception, our assumption is that it is valid error coming from our client API. So no retry.
					if (!(error instanceof HttpErrorResponse)) {
						return throwError(() => error);
					}

					// If the status code of the error is neither of those as part of our enum RetryableHttpStatus we skip retry.
					const retryableStatuses = Object.values(RetryableHttpStatus);
					const retryableStatus = retryableStatuses.includes(error.status);
					if (!retryableStatus) {
						return throwError(() => error);
					}

					if (!isIdempotent) {
						return throwError(() => error);
					}

					const retryAfter = 5;
					const retryAfterMs = retryAfter ? Number(retryAfter) * 1000 : NaN;

					const backoffMs = Math.min(10_000, this.baseDelayMs * Math.pow(2, retryCount - 1));

					const delayMs = Number.isFinite(retryAfterMs) ? retryAfterMs : backoffMs;
					return timer(delayMs);
				},
			})
		);
	}
}
